import { ApiClientError, DEFAULT_TIMEOUT } from './client';
import {
  annotateReadRecovery,
  createReadRequestId,
  recordReadRecovered,
  recordReadRetrying,
  type SafeReadOperation,
} from '@/utils/transportDiagnostics';

const RETRY_DELAY_MS = 250;

function assertActive(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException('Request aborted', 'AbortError');
}

function canRetry(error: unknown, operation: SafeReadOperation): boolean {
  return error instanceof ApiClientError && (
    (error.status === 0 && error.code === 'NETWORK_ERROR') ||
    (operation === 'party-read' && error.status >= 200 && error.status < 300 && error.code === 'PARSE_ERROR')
  );
}

function mayBeOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

function waitForRetry(signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', abort);
    };
    const abort = () => {
      cleanup();
      reject(new DOMException('Request aborted', 'AbortError'));
    };
    const timer = setTimeout(() => {
      cleanup();
      resolve();
    }, RETRY_DELAY_MS);
    signal?.addEventListener('abort', abort, { once: true });
    if (signal?.aborted) abort();
  });
}

/** 명시한 상태 조회만 복구한다. QR 집계 GET이나 쓰기 요청에는 사용하지 않는다. */
export async function recoverSafeRead<T>(
  operation: SafeReadOperation,
  signal: AbortSignal | undefined,
  read: (remainingTimeoutMs: number, requestId?: string) => Promise<T>,
): Promise<T> {
  const started = performance.now();
  const elapsed = () => performance.now() - started;
  assertActive(signal);

  let firstError: unknown;
  try {
    const requestId = createReadRequestId();
    const result = await read(DEFAULT_TIMEOUT - elapsed(), requestId);
    assertActive(signal);
    return result;
  } catch (error) {
    assertActive(signal);
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    annotateReadRecovery(error, operation, 1, elapsed());
    if (!canRetry(error, operation) || !mayBeOnline() || elapsed() + RETRY_DELAY_MS >= DEFAULT_TIMEOUT) {
      throw error;
    }
    firstError = error;
  }

  await waitForRetry(signal);
  assertActive(signal);
  const requestId = createReadRequestId();
  const remaining = DEFAULT_TIMEOUT - elapsed();
  if (!mayBeOnline() || remaining <= 0) {
    annotateReadRecovery(firstError, operation, 1, elapsed());
    throw firstError;
  }

  try {
    const pending = read(remaining, requestId);
    recordReadRetrying(operation, elapsed(), firstError);
    const result = await pending;
    assertActive(signal);
    recordReadRecovered(operation, 2, elapsed(), firstError);
    return result;
  } catch (error) {
    assertActive(signal);
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    annotateReadRecovery(error, operation, 2, elapsed());
    throw error;
  }
}
